// ─── the session socket ──────────────────────────────────────────────────────
// One WebSocket, two channels: TEXT frames are the protocol's discriminated unions,
// BINARY frames are raw audio in the currently declared format. The union types come
// from `shared/protocol.ts` — this file adds no vocabulary of its own, it only moves
// frames and reports what the socket did.

import { PROTOCOL_VERSION, decode, encode, type AudioFormat, type ClientEvent, type ServerEvent } from '@shared/protocol.ts'

export interface LinkHandlers {
  onEvent(ev: ServerEvent): void
  onAudio(frame: ArrayBuffer): void
  onOpen(): void
  /** `code` is the WebSocket close code; 1006 means the handshake or transport failed. */
  onClose(code: number, reason: string): void
}

/** Keeps hibernating Durable Objects and idle proxies from dropping a quiet session. */
const PING_INTERVAL_MS = 25_000

export class SessionLink {
  private readonly ws: WebSocket
  private ping: ReturnType<typeof setInterval> | null = null
  private closing = false
  bytesSent = 0
  bytesReceived = 0

  constructor(
    url: string,
    private readonly handlers: LinkHandlers,
    private readonly hello?: AudioFormat,
    createSocket: (url: string) => WebSocket = (address) => new WebSocket(address),
  ) {
    this.ws = createSocket(url)
    this.ws.binaryType = 'arraybuffer'

    this.ws.onopen = () => {
      if (this.closing) { this.close(); return }
      this.send({ t: 'hello', v: PROTOCOL_VERSION, audio: this.hello })
      this.ping = setInterval(() => this.send({ t: 'ping' }), PING_INTERVAL_MS)
      this.handlers.onOpen()
    }
    this.ws.onmessage = (ev) => {
      if (this.closing) return
      if (typeof ev.data !== 'string') {
        this.bytesReceived += (ev.data as ArrayBuffer).byteLength
        return this.handlers.onAudio(ev.data as ArrayBuffer)
      }
      const event = decode<ServerEvent>(ev.data)
      if (event) this.handlers.onEvent(event)
    }
    this.ws.onclose = (ev) => {
      this.stopPing()
      if (!this.closing) this.handlers.onClose(ev.code, ev.reason)
    }
    // `onerror` carries no detail by design; `onclose` always follows, so report there.
    this.ws.onerror = () => {}
  }

  get connected(): boolean {
    return this.ws.readyState === WebSocket.OPEN
  }

  send(ev: ClientEvent): void {
    if (this.ws.readyState !== WebSocket.OPEN) return
    this.ws.send(encode(ev))
  }

  sendAudio(frame: ArrayBuffer): void {
    if (this.ws.readyState !== WebSocket.OPEN) return
    this.bytesSent += frame.byteLength
    this.ws.send(frame)
  }

  close(): void {
    this.closing = true
    this.stopPing()
    try {
      this.ws.close(1000, 'client left')
    } catch {
      // already gone
    }
  }

  private stopPing(): void {
    if (this.ping) clearInterval(this.ping)
    this.ping = null
  }
}
